import React, { useEffect, useRef, useState } from "react"

import { gql, useMutation } from "urql"

import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline"

import { Color } from "~tailwindui/types/enums"

import { AlertMessageBox, Button, Spinner } from "~tailwindui/Basics"
import Modal from "~tailwindui/Modal"
import { buttonClassNames } from "~tailwindui/helpers/classNameHelpers"

import classNames from "src/classNames"

type LessonResource = {
  id: string
  name: string
  file: {
    downloadUrl: string
    filename: string
  }
}

export type CompleteWorksheetButtonsProps = {
  session: any
  refreshData: any
}

const CompleteWorksheetButtons: React.FC<CompleteWorksheetButtonsProps> = ({
  session,
  refreshData,
}) => {
  const resourceDownloadLink = useRef<HTMLAnchorElement>()
  const [downloadUrl, setDownloadUrl] = useState<string | null>()
  const [errors, setErrors] = useState<string | null>()
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    if (downloadUrl === "" || !resourceDownloadLink.current) return
    resourceDownloadLink.current.click()
  }, [downloadUrl])

  const [completeResourceState, runCompleteResource]: any[] = useMutation(
    completeLessonResourceMutation
  )

  const completeResource = (lessonResource: LessonResource) => {
    runCompleteResource({
      airtutorsSessionId: session.id,
      lessonResourceId: lessonResource.id,
    }).then(response => {
      const { failures, nextResource } = response.data.completeLessonResource
      if (failures.length > 0) {
        setErrors(failures[0].message)
        return
      }

      setErrors(null)
      refreshData({ requestPolicy: "network-only" })
      if (nextResource) {
        setDownloadUrl(nextResource.file.downloadUrl)
      }
    })
  }

  const lessonResources = session.currentLessonResources
  const resourceCount = lessonResources.length

  if (resourceCount === 0) return null

  const resourceModal = (lessonResource: LessonResource) => (
    <Modal.Dialog
      afterClose={() => {
        setErrors(null)
        setDownloadUrl(null)
      }}
      key={lessonResource.id}
      buttonText={`Complete ${lessonResource.name}`}
      buttonClassNames={classNames(
        "mb-1",
        buttonClassNames({
          color: Color.Orange,
        })
      )}
    >
      {closeModal => (
        <>
          <Modal.Header>Complete {lessonResource.name}</Modal.Header>
          <Modal.Body>
            <p className="mb-4">
              This will complete {lessonResource.name} and automatically
              download the next worksheet.
            </p>
            <p>
              Please make sure to let students know to complete the survey at
              the end of this session!
            </p>
          </Modal.Body>

          <Modal.Footer>
            <div className="flex flex-col">
              {errors && <AlertMessageBox>{errors}</AlertMessageBox>}
              <div className="flex justify-end space-x-2">
                <Button onClick={closeModal} color={Color.Red}>
                  Cancel
                </Button>
                <Button
                  disabled={completeResourceState.fetching}
                  onClick={() => completeResource(lessonResource)}
                >
                  <div className="flex space-x-2">
                    <span>Complete + Download</span>{" "}
                    {completeResourceState.fetching && <Spinner />}
                  </div>
                </Button>
              </div>
            </div>
          </Modal.Footer>
        </>
      )}
    </Modal.Dialog>
  )

  return (
    <>
      <a
        ref={resourceDownloadLink}
        href={downloadUrl}
        download
        className="hidden"
      >
        download
      </a>
      {resourceCount === 1 ? (
        resourceModal(lessonResources[0])
      ) : (
        <div className="flex flex-col">
          <button
            onClick={() => setIsOpen(prev => !prev)}
            className={classNames(
              "mb-1 flex items-center justify-center",
              buttonClassNames({ color: Color.Orange })
            )}
          >
            <span>Session Worksheets</span>
            {isOpen ? (
              <ChevronUpIcon className="ml-2 h-5 w-5 flex-shrink-0 stroke-2" />
            ) : (
              <ChevronDownIcon className="ml-2 h-5 w-5 flex-shrink-0 stroke-2" />
            )}
          </button>
          {isOpen && (
            <div className="flex flex-col">
              {lessonResources.map(lessonResource =>
                resourceModal(lessonResource)
              )}
            </div>
          )}
        </div>
      )}
    </>
  )
}

const completeLessonResourceMutation = gql`
  mutation ($airtutorsSessionId: ID!, $lessonResourceId: ID!) {
    completeLessonResource(
      airtutorsSessionId: $airtutorsSessionId
      lessonResourceId: $lessonResourceId
    ) {
      failures {
        message
      }
      nextResource {
        id
        name
        file {
          downloadUrl
          filename
        }
      }
    }
  }
`

export default CompleteWorksheetButtons
